import api from './api';
import axios from 'axios';
import { EmployeeDetails } from '../types';

export interface Employee {
    id: string;
    created_at: string;
    updated_at: string;
    email: string;
    password?: string;
    invite_code: string | null;
    department: string;
    name: string;
    role: string;
    image: string;
    image_url: string | null;
    imageUrl?: string;
    location: string;
    phone: string | null;
    status: 'active' | 'inactive';
    completedCourses: number;
    activeCourses: number;
    completedDate?: string;
    last_sign_in?: string;  // X Days ago format from backend
}

export interface EmployeeTraining {
    id: string;
    name: string;
    role: string;
    image: string;
    pendingCourses: string[];
    progress: number;
    dueDate: string;
}

export interface EmployeeTrainingResponse {
    data: EmployeeTraining[];
}

interface InviteEmployeeData {
    email: string;
    name: string;
    curriculum_id: string;
}

export interface AssignedCourse {
    id: string;
    name: string;
    progress: number;
    status: 'Completed' | 'In Progress' | 'Not Started';
    dueDate: string;
}

export interface ChecklistItem {
    id: string;
    topic: string;
    requiredBy: string;
    category: string;
    deadlineRule: string;
    status: 'Completed' | 'Pending' | 'N/A';
    completionDate?: string;
    dueDate: string;
}


export const employeeService = {
    getAllEmployees: async (): Promise<Employee[]> => {
        const response = await api.get('admin/employees');
        return response.data;
    },

    getEmployeeStats: async () => {
        const response = await api.get('admin/dashboard-stats');
        return response.data;
    },

    getEmployeeTraining: async (): Promise<EmployeeTrainingResponse> => {
        const response = await api.get('/admin/training-overview');
        return response.data;
    },

    getPendingInvitations: async () => {
        try {
            const response = await api.get('/admin/pending-invitations');
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to fetch pending invitations');
            }
            throw error;
        }
    },

    inviteEmployee: async (data: InviteEmployeeData) => {
        try {
            const response = await api.post('/admin/invite', data);
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to invite employee');
            }
            throw error;
        }
    },

    assignCurriculum: async (userId: string, curriculumId: string) => {
        try {
            const response = await api.post('/admin/assign-curriculum', { userId, curriculumId });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to assign curriculum');
            }
            throw error;
        }
    },

    sendBulkReminders: async (emails: string[]) => {
        try {
            const response = await api.post('/admin/bulk-reminder', { emails });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.message || 'Failed to send bulk reminders');
            }
            throw error;
        }
    },

    updateProfile: async (name: string): Promise<void> => {
        try {
            const response = await api.put('/users/profile', { name });
            return response.data;
        } catch (error) {
            console.error('Error updating profile:', error);
            throw error;
        }
    },

    deactivateEmployee: async (employeeId: string): Promise<void> => {
        try {
            const response = await api.post('/admin/employees/deactivate', { employeeId });
            return response.data;
        } catch (error) {
            console.error('Error deactivating employee:', error);
            throw new Error('Failed to deactivate employee');
        }
    },

    reactivateEmployee: async (employeeId: string): Promise<void> => {
        try {
            const response = await api.post('/admin/employees/reactivate', { employeeId });
            return response.data;
        } catch (error) {
            console.error('Error reactivating employee:', error);
            throw new Error('Failed to reactivate employee');
        }
    },

    getEmployeeDetails: async (employeeId: string): Promise<EmployeeDetails> => {
        try {
            const response = await api.get(`/admin/employees/${employeeId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching employee details:', error);
            if (axios.isAxiosError(error)) {
                if (error.response?.status === 404) {
                    throw new Error('Employee not found');
                }
                throw new Error(error.response?.data?.message || 'Failed to fetch employee details');
            }
            throw new Error('Failed to fetch employee details');
        }
    }
}; 